import { useCallback, useEffect, useRef, useState } from "react";
import { FmFileImage } from "domains/file-manager/interfacesV2";
import ModalLibraryAssetSelect from "domains/image/components/ModalLibraryAssetSelect";
import { DescriptionSection } from "domains/models/components/ModelShow/Details/DescriptionSection";
import { ImageSection } from "domains/models/components/ModelShow/Details/ImageSection";
import { ModelStatusSection } from "domains/models/components/ModelShow/Details/ModelStatusSection";
import { RecommendationsSection } from "domains/models/components/ModelShow/Details/RecommendationsSection";
import useRouter from "domains/navigation/hooks/useRouter";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelsByModelIdApiResponse,
  useGetModelsExamplesByModelIdQuery,
  useGetRecommendationsModelsQuery,
  usePutModelsByModelIdMutation,
  usePutModelsExamplesByModelIdMutation,
} from "infra/api/generated/api";

import { Divider, Flex, useDisclosure } from "@chakra-ui/react";

const ModelShowDetails = ({
  model,
  isOnLoraCatalog,
}: {
  model: GetModelsByModelIdApiResponse["model"];
  isOnLoraCatalog?: boolean;
}) => {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const { successToast } = useScenarioToast();
  const handleApiError = useHandleApiError();

  const modelRecommendationsRef = useRef<HTMLDivElement>(null);
  const [numberOfRecommendations, setNumberOfRecommendations] = useState(5); // Default to 5

  const [putModelsExamplesByModelId] = usePutModelsExamplesByModelIdMutation();
  const { data: modelExamples, isLoading: isLoadingModelExamples } =
    useGetModelsExamplesByModelIdQuery({
      projectId: selectedProject.id,
      modelId: model.id,
    });

  const [updateModelTrigger, { isLoading: isUpdatingThumbnail }] =
    usePutModelsByModelIdMutation();

  const {
    isOpen: isAssetSelectorOpen,
    onOpen: onOpenAssetSelector,
    onClose: onCloseAssetSelector,
  } = useDisclosure();

  const [updateAction, setUpdateAction] = useState<{
    type: "thumbnail" | "example";
    index?: number;
  }>();

  const onOpenAssetSelectorAndSetIndex = (index: number) => {
    setUpdateAction({ type: "example", index });
    onOpenAssetSelector();
  };

  const onOpenAssetSelectorForThumbnail = () => {
    setUpdateAction({ type: "thumbnail" });
    onOpenAssetSelector();
  };

  const {
    data: modelRecommendations,
    isLoading: isLoadingModelRecommendations,
  } = useGetRecommendationsModelsQuery({
    projectId: selectedProject.id,
    limit: numberOfRecommendations.toString(),
    excludeModelIds: model.id,
    tags: model.tags.join(", "),
    modelType: model.type,
  });

  useEffect(() => {
    const updateNumberOfRecommendations = () => {
      if (modelRecommendationsRef.current) {
        const width = modelRecommendationsRef.current.offsetWidth;
        const calculatedRecommendations = Math.floor(width / 200);
        // -1 because we add a first card displaying "Create composition"
        setNumberOfRecommendations(calculatedRecommendations);
      }
    };

    updateNumberOfRecommendations();
    window.addEventListener("resize", updateNumberOfRecommendations);
    return () =>
      window.removeEventListener("resize", updateNumberOfRecommendations);
  }, [model.status, modelRecommendationsRef]);

  const handleUpdateThumbnail = useCallback(
    async (asset: GetAssetsByAssetIdApiResponse["asset"]) => {
      try {
        await updateModelTrigger({
          modelId: model.id,
          body: {
            thumbnail: asset.id,
          },
          projectId: selectedProject.id,
        }).unwrap();
        successToast({
          title: "Thumbnail updated",
        });
        Track(AnalyticsEvents.Model.UploadedThumbnail, {
          modelId: model.id,
        });
      } catch (error) {
        handleApiError(
          error,
          "There was an error updating the model's thumbnail"
        );
      }
      onCloseAssetSelector();
    },
    [
      handleApiError,
      model.id,
      onCloseAssetSelector,
      selectedProject.id,
      successToast,
      updateModelTrigger,
    ]
  );

  const handleAddExample = useCallback(
    async (asset: FmFileImage, index: number) => {
      try {
        const assetIds =
          modelExamples?.examples?.map((example) => example.asset.id) ?? [];

        if (index >= 0 && index < assetIds.length) {
          assetIds[index] = asset.id;
        } else {
          assetIds.push(asset.id);
        }

        await putModelsExamplesByModelId({
          modelId: model.id,
          projectId: selectedProject.id,
          body: {
            assetIds,
          },
        });
        successToast({
          title: "Pinned image updated",
        });
      } catch (error) {
        handleApiError(error, "There was an error updating the pinned image");
      }
      onCloseAssetSelector();
    },
    [
      model.id,
      onCloseAssetSelector,
      modelExamples?.examples,
      putModelsExamplesByModelId,
      selectedProject.id,
      successToast,
      handleApiError,
    ]
  );

  const handleOnRemoveExample = useCallback(
    async (indexImage: number) => {
      if (!model.id) return;
      try {
        // Filter out the asset to remove by index, then map to get the asset IDs
        const updatedAssetIds =
          modelExamples?.examples
            ?.filter((_, index) => index !== indexImage)
            .map((example) => example.asset.id) ?? [];
        await putModelsExamplesByModelId({
          modelId: model.id,
          projectId: selectedProject.id,
          body: {
            assetIds: updatedAssetIds ?? [],
          },
        });
        successToast({
          title: "Pinned image removed",
        });
      } catch (error) {
        handleApiError(error, "There was an error removing the pinned image");
      }
      onCloseAssetSelector();
    },
    [
      model.id,
      onCloseAssetSelector,
      modelExamples?.examples,
      putModelsExamplesByModelId,
      selectedProject.id,
      successToast,
      handleApiError,
    ]
  );

  const handleOnSeeMore = useCallback(
    (tab: string) => {
      void router.push({
        query: { ...router.query, tab: tab },
      });
    },
    [router]
  );

  const handleOnSeeMoreSettings = () => handleOnSeeMore("details");
  const handleOnSeeMoreImages = () => handleOnSeeMore("images");

  return (
    <Flex direction="column" w="100%" h="100%" py={2}>
      <ModelStatusSection model={model} />
      <Flex h="full">
        <ImageSection
          model={model}
          modelExamples={modelExamples}
          isUpdatingThumbnail={isUpdatingThumbnail}
          isLoadingModelExamples={isLoadingModelExamples}
          isOnLoraCatalog={isOnLoraCatalog}
          onOpenAssetSelectorForThumbnail={onOpenAssetSelectorForThumbnail}
          onOpenAssetSelectorAndSetIndex={onOpenAssetSelectorAndSetIndex}
          handleOnSeeMore={handleOnSeeMoreImages}
          handleOnRemoveExample={handleOnRemoveExample}
          onUpdateThumbnail={handleUpdateThumbnail}
        />
        <DescriptionSection
          isOnLoraCatalog={isOnLoraCatalog ?? false}
          handleOnSeeMore={handleOnSeeMoreSettings}
          model={model}
        />
      </Flex>
      <Divider py={2} />
      <Flex ref={modelRecommendationsRef}>
        <RecommendationsSection
          model={model}
          isLoadingModelRecommendations={isLoadingModelRecommendations}
          modelRecommendations={modelRecommendations}
          isOnLoraCatalog={isOnLoraCatalog ?? false}
        />
      </Flex>

      <ModalLibraryAssetSelect
        isOpen={isAssetSelectorOpen}
        onClose={onCloseAssetSelector}
        onSelect={(asset: FmFileImage) => {
          if (
            updateAction?.type === "example" &&
            typeof updateAction.index === "number"
          ) {
            void handleAddExample(asset, updateAction.index);
          } else if (updateAction?.type === "thumbnail") {
            void handleUpdateThumbnail(asset.meta);
          }
        }}
        modelId={model.id}
        tabs={["image"]}
      />
    </Flex>
  );
};

export default ModelShowDetails;
